import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { LogoMark } from "@/components/relyn/Nav";
import Footer from "@/components/relyn/Footer";
import useCanonical from "@/hooks/useCanonical";

export default function LegalLayout({
  title,
  intro,
  toc,
  effectiveDate,
  lastUpdated,
  children,
  testid = "legal-page",
}) {
  useCanonical();
  const [active, setActive] = useState(toc?.[0]?.id);

  useEffect(() => {
    if (!toc?.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id);
        });
      },
      { rootMargin: "-40% 0px -50% 0px", threshold: 0 }
    );
    toc.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [toc]);

  return (
    <div className="relative min-h-screen bg-[#0A1A2F] text-white" data-testid={testid}>
      {/* Top bar */}
      <header className="sticky top-0 z-40 backdrop-blur-md bg-[#0A1A2F]/85 border-b border-white/[0.06]">
        <div className="max-w-6xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-2.5 hover:opacity-90 transition-opacity"
            data-testid="legal-logo-home"
          >
            <LogoMark size={36} />
            <span
              className="text-white text-lg tracking-tight"
              style={{ fontFamily: "Outfit", fontWeight: 600 }}
            >
              Relyn
            </span>
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-[#C0C7D1] hover:text-white transition-colors"
            data-testid="legal-back-home"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
        </div>
      </header>

      <main className="relative">
        {/* Ambient */}
        <div className="pointer-events-none absolute inset-x-0 top-0 h-[420px] overflow-hidden" aria-hidden="true">
          <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[1200px] h-[600px] bg-[#2D8CFF]/12 blur-3xl rounded-full" />
        </div>

        <div className="relative max-w-6xl mx-auto px-5 md:px-8 pt-12 md:pt-16 pb-16">
          {/* Title */}
          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.28em] text-[#5aa8ff]" style={{ fontFamily: "Outfit" }}>
              Relyn Legal
            </p>
            <h1
              className="mt-3 text-4xl md:text-5xl lg:text-6xl font-light tracking-tight leading-[1.05]"
              data-testid="legal-title"
            >
              {title}
            </h1>
            {intro ? (
              <p className="mt-5 text-base md:text-lg text-[#C0C7D1] leading-relaxed">{intro}</p>
            ) : null}
            <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-xs text-[#8A99B0]" data-testid="legal-dates">
              {effectiveDate ? (
                <span>
                  <span className="text-[#5aa8ff] uppercase tracking-[0.22em] mr-2">Effective</span>
                  {effectiveDate}
                </span>
              ) : null}
              {lastUpdated ? (
                <span>
                  <span className="text-[#5aa8ff] uppercase tracking-[0.22em] mr-2">Last updated</span>
                  {lastUpdated}
                </span>
              ) : null}
            </div>
          </div>

          {/* Body + TOC */}
          <div className="mt-10 md:mt-14 grid lg:grid-cols-[240px_1fr] gap-10">
            {/* TOC (desktop sticky) */}
            <aside className="hidden lg:block">
              <nav
                aria-label="Table of contents"
                className="sticky top-24 glass rounded-2xl p-5"
                data-testid="legal-toc"
              >
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#5aa8ff]">On this page</p>
                <ul className="mt-3 space-y-2">
                  {toc.map((s) => (
                    <li key={s.id}>
                      <a
                        href={`#${s.id}`}
                        className={`block text-sm leading-snug transition-colors ${
                          active === s.id
                            ? "text-white"
                            : "text-[#C0C7D1] hover:text-white"
                        }`}
                        data-testid={`legal-toc-link-${s.id}`}
                      >
                        <span
                          className={`inline-block w-1 h-1 rounded-full mr-2 align-middle ${
                            active === s.id ? "bg-[#2D8CFF]" : "bg-white/20"
                          }`}
                        />
                        {s.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>
            </aside>

            {/* TOC (mobile inline) */}
            <details className="lg:hidden glass rounded-2xl p-4" data-testid="legal-toc-mobile">
              <summary className="cursor-pointer list-none flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-[0.22em] text-[#5aa8ff]">Contents</span>
                <span className="text-[#C0C7D1] text-sm">Jump to section</span>
              </summary>
              <ul className="mt-3 space-y-2">
                {toc.map((s) => (
                  <li key={s.id}>
                    <a
                      href={`#${s.id}`}
                      className="block text-sm text-[#C0C7D1] hover:text-white transition-colors"
                    >
                      {s.label}
                    </a>
                  </li>
                ))}
              </ul>
            </details>

            {/* Content */}
            <article className="legal-prose text-[#C0C7D1] leading-relaxed max-w-3xl">
              {children}
            </article>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

/* --------------- Helpers used by legal pages --------------- */
export function LegalSection({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-24 mt-12 first:mt-0" data-testid={`legal-section-${id}`}>
      <h2
        className="text-2xl md:text-3xl font-medium text-white tracking-tight"
        style={{ fontFamily: "Outfit" }}
      >
        {title}
      </h2>
      <div className="mt-4 space-y-4 text-[15px] md:text-base">{children}</div>
    </section>
  );
}

export function LegalList({ items }) {
  return (
    <ul className="space-y-2.5 list-none pl-0">
      {items.map((it, i) => (
        <li key={i} className="pl-5 relative">
          <span className="absolute left-0 top-2 w-1.5 h-1.5 rounded-full bg-[#2D8CFF]" />
          {it}
        </li>
      ))}
    </ul>
  );
}

export function ContactBlock() {
  return (
    <div className="mt-6 glass rounded-2xl p-5 md:p-6">
      <p className="text-[10px] uppercase tracking-[0.22em] text-[#5aa8ff]">Contact</p>
      <p className="mt-2 text-white">
        For legal and privacy inquiries, please reach us at{" "}
        <a
          href="mailto:support@relynapp.com"
          className="text-[#5aa8ff] hover:text-white transition-colors underline decoration-[#2D8CFF]/40 underline-offset-4"
          data-testid="legal-contact-email"
        >
          support@relynapp.com
        </a>
        .
      </p>
    </div>
  );
}
